import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const CYAN = '#00F3FF';
const PURPLE = '#BC13FE';
const BG = '#030305';
const CARD_BG = '#0a0a12';
const BORDER = '#1a1a2e';
const GOLD = '#FFD700';

const styles = {
  page: {
    background: BG,
    minHeight: '100vh',
    color: '#e0e0e0',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    paddingTop: 100,
    paddingBottom: 80,
  },
  hero: {
    textAlign: 'center',
    padding: '60px 20px 40px',
    background: 'linear-gradient(180deg, #0a0005 0%, #030305 100%)',
    position: 'relative',
    overflow: 'hidden',
  },
  heroBadge: {
    display: 'inline-block',
    background: `linear-gradient(135deg, ${PURPLE}22, ${CYAN}22)`,
    border: `1px solid ${PURPLE}66`,
    borderRadius: 30,
    padding: '6px 20px',
    fontSize: 12,
    letterSpacing: 3,
    color: PURPLE,
    fontWeight: 700,
    marginBottom: 20,
    textTransform: 'uppercase',
  },
  heroTitle: {
    fontSize: 'clamp(2rem, 5vw, 3.5rem)',
    fontWeight: 900,
    margin: '0 0 16px',
    background: `linear-gradient(135deg, #fff 0%, ${PURPLE} 50%, ${CYAN} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    lineHeight: 1.15,
  },
  heroSub: {
    color: '#888',
    fontSize: '1.1rem',
    maxWidth: 640,
    margin: '0 auto 30px',
    lineHeight: 1.7,
  },
  priceBadge: {
    display: 'inline-block',
    background: `linear-gradient(135deg, ${PURPLE}33, ${CYAN}22)`,
    border: `1px solid ${PURPLE}88`,
    borderRadius: 30,
    padding: '8px 24px',
    fontSize: 16,
    color: '#fff',
    fontWeight: 700,
  },
  glowOrb: {
    position: 'absolute',
    borderRadius: '50%',
    filter: 'blur(80px)',
    opacity: 0.16,
    pointerEvents: 'none',
  },
  launchBanner: {
    maxWidth: 900,
    margin: '32px auto 0',
    padding: '0 20px',
  },
  launchBannerInner: {
    background: `linear-gradient(135deg, ${GOLD}18, ${PURPLE}18)`,
    border: `1px solid ${GOLD}55`,
    borderRadius: 12,
    padding: '14px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    flexWrap: 'wrap',
  },
  launchIcon: {
    fontSize: 22,
  },
  launchText: {
    flex: 1,
    fontSize: 14,
    color: '#ddd',
    lineHeight: 1.5,
  },
  launchHighlight: {
    color: GOLD,
    fontWeight: 800,
  },
  section: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '40px 20px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  sectionTitle: {
    fontSize: '1.35rem',
    fontWeight: 800,
    color: '#fff',
    margin: 0,
  },
  sectionTag: (color) => ({
    background: `${color}18`,
    border: `1px solid ${color}44`,
    borderRadius: 20,
    padding: '3px 12px',
    fontSize: 11,
    color: color,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: 'uppercase',
  }),
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
    gap: 20,
  },
  card: {
    background: CARD_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: 16,
    padding: '22px 20px 18px',
    transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
    cursor: 'default',
    position: 'relative',
    overflow: 'hidden',
  },
  cardHover: {
    transform: 'translateY(-6px)',
    boxShadow: `0 20px 50px ${PURPLE}1a`,
    borderColor: `${PURPLE}55`,
  },
  vramBadge: (color) => ({
    display: 'inline-block',
    background: `${color}22`,
    border: `1px solid ${color}55`,
    borderRadius: 20,
    padding: '3px 12px',
    fontSize: 11,
    fontWeight: 700,
    color: color,
    letterSpacing: 1,
    marginBottom: 10,
    textTransform: 'uppercase',
  }),
  cardTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#fff',
    marginBottom: 14,
  },
  specTable: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: 14,
  },
  specRow: {
    borderBottom: `1px solid ${BORDER}`,
  },
  specKey: {
    padding: '6px 2px',
    color: '#555',
    fontSize: 12,
    width: '44%',
  },
  specVal: {
    padding: '6px 2px',
    color: '#bbb',
    fontSize: 12,
    fontWeight: 600,
    textAlign: 'right',
  },
  price: (color) => ({
    fontSize: '1.5rem',
    fontWeight: 900,
    color: color || CYAN,
    margin: '12px 0 3px',
  }),
  priceNote: {
    fontSize: 11,
    color: '#444',
    marginBottom: 16,
  },
  btnRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  waBtn: {
    flex: 1,
    minWidth: 100,
    padding: '9px 10px',
    background: 'linear-gradient(135deg, #25D366, #128C7E)',
    border: 'none',
    borderRadius: 9,
    color: '#fff',
    fontWeight: 700,
    fontSize: 12,
    cursor: 'pointer',
    textAlign: 'center',
    textDecoration: 'none',
    display: 'inline-block',
  },
  quoteBtn: (color) => ({
    flex: 1,
    minWidth: 100,
    padding: '9px 10px',
    background: `${color}18`,
    border: `1px solid ${color}55`,
    borderRadius: 9,
    color: color,
    fontWeight: 700,
    fontSize: 12,
    textAlign: 'center',
    textDecoration: 'none',
    display: 'inline-block',
  }),
  gpuSpecBox: {
    background: CARD_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: 16,
    padding: '28px 28px 24px',
    maxWidth: 1200,
    margin: '0 auto 60px',
  },
  gpuSpecGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: 14,
    marginTop: 22,
  },
  gpuSpecItem: {
    background: '#0f0f1a',
    border: `1px solid ${BORDER}`,
    borderRadius: 11,
    padding: '14px 14px',
    textAlign: 'center',
  },
  gpuSpecLabel: {
    fontSize: 11,
    color: '#555',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  gpuSpecValue: {
    fontSize: '1rem',
    fontWeight: 800,
    color: PURPLE,
  },
};

const openWA = (planName) => {
  const text = encodeURIComponent(`Hi, I'm interested in the ${planName} plan on IndiaCloud. Please share more details.`);
  window.open(`https://wa.me/919873385555?text=${text}`, '_blank');
};

function PlanCard({ plan, accentColor }) {
  const [hovered, setHovered] = useState(false);
  const color = accentColor || PURPLE;
  return (
    <div
      style={{ ...styles.card, ...(hovered ? styles.cardHover : {}) }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ position: 'absolute', top: 0, right: 0, width: 100, height: 100,
        background: `radial-gradient(circle at top right, ${color}18, transparent 70%)`, pointerEvents: 'none' }} />
      <div style={styles.vramBadge(color)}>{plan.badge}</div>
      <div style={styles.cardTitle}>{plan.name}</div>
      <table style={styles.specTable}>
        <tbody>
          {plan.specs.map(([k, v]) => (
            <tr key={k} style={styles.specRow}>
              <td style={styles.specKey}>{k}</td>
              <td style={styles.specVal}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={styles.price(color)}>{plan.price}</div>
      <div style={styles.priceNote}>{plan.note}</div>
      <div style={styles.btnRow}>
        <button style={styles.waBtn} onClick={() => openWA(plan.name)}>💬 WhatsApp</button>
        <Link to="/contact" style={styles.quoteBtn(color)}>Get Quote →</Link>
      </div>
    </div>
  );
}

const singleGpuPlans = [
  {
    name: 'AI Starter',
    badge: '12GB VRAM',
    price: '₹12,999/mo',
    note: '+ GST • 1× RTX PRO 6000',
    specs: [['VRAM', '12 GB'], ['vCPU', '8 Cores'], ['RAM', '16 GB'], ['NVMe', '300 GB'], ['Network', '1 Gbps'], ['IPv4', 'Included']],
  },
  {
    name: 'AI Professional',
    badge: '24GB VRAM',
    price: '₹24,999/mo',
    note: '+ GST • 1× RTX PRO 6000',
    specs: [['VRAM', '24 GB'], ['vCPU', '16 Cores'], ['RAM', '32 GB'], ['NVMe', '600 GB'], ['Network', '1 Gbps'], ['IPv4', 'Included']],
  },
  {
    name: 'AI Advanced',
    badge: '48GB VRAM',
    price: '₹44,999/mo',
    note: '+ GST • 1× RTX PRO 6000',
    specs: [['VRAM', '48 GB'], ['vCPU', '24 Cores'], ['RAM', '64 GB'], ['NVMe', '1 TB'], ['Network', '1 Gbps'], ['IPv4', 'Included']],
  },
  {
    name: 'AI Enterprise',
    badge: '72GB VRAM',
    price: '₹74,999/mo',
    note: '+ GST • 1× RTX PRO 6000',
    specs: [['VRAM', '72 GB'], ['vCPU', '32 Cores'], ['RAM', '128 GB'], ['NVMe', '2 TB'], ['Network', '1 Gbps'], ['IPv4', 'Included']],
  },
  {
    name: 'AI Dedicated',
    badge: '96GB VRAM',
    price: '₹1,19,999/mo',
    note: '+ GST • 1× RTX PRO 6000',
    specs: [['VRAM', '96 GB'], ['vCPU', '48 Cores'], ['RAM', '256 GB'], ['NVMe', '4 TB'], ['Network', '1 Gbps'], ['IPv4', 'Included']],
  },
];

const multiGpuPlans = [
  {
    name: '2× RTX PRO 6000 Cloud',
    badge: '2× GPU / 192GB',
    price: '₹2,29,999/mo',
    note: '+ GST • Dedicated Multi-GPU',
    specs: [['GPUs', '2× RTX PRO 6000'], ['VRAM', '192 GB'], ['vCPU', '96 Cores'], ['RAM', '512 GB'], ['NVMe', '8 TB']],
  },
  {
    name: '4× RTX PRO 6000 Cloud',
    badge: '4× GPU / 384GB',
    price: '₹4,49,999/mo',
    note: '+ GST • Dedicated Multi-GPU',
    specs: [['GPUs', '4× RTX PRO 6000'], ['VRAM', '384 GB'], ['vCPU', '128 Cores'], ['RAM', '1 TB'], ['NVMe', '16 TB'], ['Network', 'High-Speed']],
  },
  {
    name: '6× RTX PRO 6000 Cloud',
    badge: '6× GPU / 576GB',
    price: '₹6,49,999/mo',
    note: '+ GST • Dedicated Multi-GPU',
    specs: [['GPUs', '6× RTX PRO 6000'], ['VRAM', '576 GB'], ['vCPU', '160 Cores'], ['RAM', '1.5 TB'], ['NVMe', '24 TB'], ['Network', 'Dedicated']],
  },
  {
    name: '8× RTX PRO 6000 Bare Metal',
    badge: '8× GPU / 768GB',
    price: '₹8,49,999/mo',
    note: '+ GST • Dedicated Bare Metal',
    specs: [['GPUs', '8× RTX PRO 6000'], ['VRAM', '768 GB'], ['vCPU', '192 Cores'], ['RAM', '3 TB'], ['NVMe', '48 TB'], ['Network', 'Dual 100Gb']],
  },
];

const gpuSpecs = [
  { label: 'GPU Model', value: 'RTX PRO 6000' },
  { label: 'Architecture', value: 'Blackwell' },
  { label: 'VRAM', value: '96GB GDDR7' },
  { label: 'CUDA Cores', value: '24,576' },
  { label: 'RT Cores', value: '192' },
  { label: 'Tensor Cores', value: '768' },
  { label: 'Memory BW', value: '1.8 TB/s' },
  { label: 'FP32', value: '125 TFLOPS' },
  { label: 'FP16 (est)', value: '250 TFLOPS' },
];

export default function RtxBlackwell() {
  return (
    <div style={styles.page}>
      {/* Hero */}
      <div style={styles.hero}>
        <div style={{ ...styles.glowOrb, width: 500, height: 500, background: PURPLE, top: -200, left: '5%' }} />
        <div style={{ ...styles.glowOrb, width: 400, height: 400, background: CYAN, top: -160, right: '5%' }} />
        <div style={styles.heroBadge}>RTX PRO 6000 BLACKWELL</div>
        <h1 style={styles.heroTitle}>RTX PRO 6000<br />Blackwell GPU Cloud</h1>
        <p style={styles.heroSub}>
          96GB GDDR7 with 1.8 TB/s bandwidth and 24,576 CUDA cores — next-generation AI workloads at unmatched scale.
        </p>
        <div style={styles.priceBadge}>Starting at ₹12,999/mo + GST</div>
      </div>

      {/* Launch Offer Banner */}
      <div style={styles.launchBanner}>
        <div style={styles.launchBannerInner}>
          <span style={styles.launchIcon}>🎉</span>
          <div style={styles.launchText}>
            <span style={styles.launchHighlight}>Launch Offer:</span> First month setup fee <span style={styles.launchHighlight}>WAIVED</span> on all RTX PRO 6000 Blackwell plans. Limited time — contact us to avail.
          </div>
          <button
            style={{ ...styles.waBtn, flex: 'none', padding: '9px 18px', fontSize: 13, borderRadius: 9 }}
            onClick={() => openWA('RTX PRO 6000 Blackwell Launch Offer')}
          >
            💬 Claim Offer
          </button>
        </div>
      </div>

      {/* Section A: Single GPU Plans */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Single GPU Plans</h2>
          <span style={styles.sectionTag(PURPLE)}>Single GPU</span>
        </div>
        <div style={styles.cardsGrid}>
          {singleGpuPlans.map((p) => <PlanCard key={p.name} plan={p} accentColor={PURPLE} />)}
        </div>
      </div>

      {/* Section B: Multi-GPU Dedicated */}
      <div style={{ ...styles.section, paddingTop: 0 }}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Multi-GPU Dedicated AI Cloud</h2>
          <span style={styles.sectionTag(CYAN)}>Multi-GPU</span>
          <span style={styles.sectionTag(GOLD)}>Dedicated</span>
        </div>
        <div style={styles.cardsGrid}>
          {multiGpuPlans.map((p) => <PlanCard key={p.name} plan={p} accentColor={CYAN} />)}
        </div>
      </div>

      {/* GPU Spec */}
      <div style={{ padding: '0 20px' }}>
        <div style={styles.gpuSpecBox}>
          <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff', margin: '0 0 4px' }}>GPU Specifications</h2>
          <div style={{ color: '#555', fontSize: 12 }}>RTX PRO 6000 — Blackwell Architecture</div>
          <div style={styles.gpuSpecGrid}>
            {gpuSpecs.map((s) => (
              <div key={s.label} style={styles.gpuSpecItem}>
                <div style={styles.gpuSpecLabel}>{s.label}</div>
                <div style={styles.gpuSpecValue}>{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
